import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import Head from 'next/head';
import cx from 'classnames';

import { IconButton, Image, Link, Spinner } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import { Page } from 'components/layouts';
import { MatchesProgress } from 'components/candidate/matches';
import { getLayout } from 'components/layouts/Header';
import { TodoSection } from 'pages/candidate/dashboard/TodoSection/TodoSection.component';
import { withAuthCandidate } from 'lib/hocs/withAuthCandidate';
import { LOCALE_PAGES, fetchTranslationFiles } from 'lib/services/page';
import { useCandidateMatches } from 'lib/providers/CandidateMatchesProvider';
import { PAGE_ROUTES } from 'lib/page-routes';

import styles from '../NoMatches.module.scss';

const CandidateMatchesSuccess = () => {
  const [t] = useTranslation('candidate.matches.success');
  const {
    matchesMeta,
    opportunityMatches,
    generateNewMatches,
    isMatchesLoading,
  } = useCandidateMatches();
  const { replace, push } = useRouter();

  useEffect(() => {
    if (!matchesMeta) return;
    const { total_count, viewed_count } = matchesMeta;
    if (viewed_count < total_count) {
      replace(PAGE_ROUTES.CANDIDATE_MATCHES);
    }
  }, [matchesMeta]);

  const handlePrev = useCallback(() => {
    if (opportunityMatches.length === 0) return;
    const lastMatch = opportunityMatches[opportunityMatches.length - 1];
    if (!lastMatch) return;
    const route = `${PAGE_ROUTES.CANDIDATE_MATCHES}/${lastMatch.matchable_slug}`;
    push(route);
  }, [opportunityMatches, push]);

  useEffect(() => {
    function handleKeydown(event: KeyboardEvent) {
      switch (event.key) {
        case 'ArrowLeft':
          if (handlePrev) handlePrev();
          break;
      }
    }

    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [handlePrev]);

  const handleGetMoreMatches = useCallback(async () => {
    try {
      push(PAGE_ROUTES.CANDIDATE_MATCHES_GENERATING);
      await generateNewMatches();
    } catch (error) {
      console.error(error);
    }
  }, [generateNewMatches]);

  if (isMatchesLoading) return <Spinner wrapperClassname={styles.spinner} />;

  return (
    <>
      <Head>
        <title>Matches | Huzzle</title>
      </Head>
      <Page className={styles.page}>
        <MatchesProgress className={styles['matches-progress']} />
        <section className={styles.container}>
          <div className={styles['nav-btn-container']}>
            <IconButton
              iconName="icon_arrow-backward"
              size="medium"
              className={cx(styles['nav-btn'], styles['nav-btn--prev'])}
              onClick={handlePrev}
            />
          </div>
          <div className={styles.content}>
            <Image
              src={'/images/me-success.png'}
              width="100%"
              className={styles.image}
            />
            <div className={styles['main']}>
              <p className={styles.emoji}>{t('emoji')}</p>
              <p className={styles.title}>{t('title')}</p>
              <p className={styles.description}>
                <span>{t('description-line1')}</span>
                <span>{t('description-line2')}</span>
              </p>
              <div className={styles.actions}>
                <ButtonV3
                  color="primary"
                  size="large"
                  onClick={handleGetMoreMatches}
                  className={styles.btn}
                >
                  {t('view-more-matches')}
                </ButtonV3>
                <Link
                  href={PAGE_ROUTES.CANDIDATE_DASHBOARD}
                  underlineOnHover={false}
                  className={styles.btn}
                >
                  <ButtonV3
                    color="secondary"
                    size="large"
                    className={styles.btn}
                  >
                    {t('go-to-home')}
                  </ButtonV3>
                </Link>
              </div>
              <p className={styles['link-para']}>
                <Trans t={t} i18nKey={t('link-para')}>
                  Want different Matches?{' '}
                  <Link
                    href={`${PAGE_ROUTES.CANDIDATE_ONBOARDING}?matching_preference=true`}
                    className={styles.link}
                  >
                    Change Preferences
                  </Link>
                </Trans>
              </p>
            </div>
            <TodoSection className={styles['todo-section-card']} />
          </div>
          <div className={styles['nav-btn-container']}>
            <IconButton
              iconName="icon_arrow-forward"
              size="medium"
              className={cx(styles['nav-btn'], styles['nav-btn--next'])}
            />
          </div>
        </section>
      </Page>
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await fetchTranslationFiles({
        locale,
        pages: [LOCALE_PAGES.CANDIDATE_MATCHES_SUCCESS],
      })),
    },
  };
}

const CandidateMatchesSuccessWrapper = withAuthCandidate(
  CandidateMatchesSuccess
);

// @ts-ignore
CandidateMatchesSuccessWrapper.getLayout = getLayout;

export default CandidateMatchesSuccessWrapper;
